<script setup>
import IntroLayout from "@/Layouts/IntroLayout.vue";
import {route} from "@/Scripts/Routing/Router.js";
import {computed, ref} from "vue";
import {Link, usePage} from '@inertiajs/inertia-vue3';
import LoginForm from "@/Components/Home/LoginForm.vue";
import PageFooter from "@/Components/Home/PageFooter.vue";
import DiscoverIcon from "@/Components/Home/DiscoverIcon.vue";
import PublishIcon from "@/Components/Home/PublishIcon.vue";
import CommunicateIcon from "@/Components/Home/CommunicateIcon.vue";
import ArticleCard from "@/Components/Articles/ArticleCard.vue";
import FancyButton from "@/Components/Buttons/FancyButton.vue";
import EventPressLogo from "@/Components/Home/EventPressLogo.vue";
import FleetPressLogo from "@/Components/Home/FleetPressLogo.vue";
import Tooltip from "@/Components/Tooltip.vue";
import FeatureWarning from "@/Components/FeatureWarning.vue";
import {useFeatures} from "@/Scripts/Routing/Composables/useFeatures.js";
import FlashMessage from "@/Components/FlashMessage.vue";

const props = defineProps({
    errors: Object,
    profile: Object,
    app: Object,
    status: String,
    articles: Array,
    galleries: Array,
    videos: Array,
    organisations: Array,
    introVideo: String,
    introVideoPoster: String,
    tv: Object,
    content: Object,
    release: Number,
});

const tv = useFeatures().get('tv');

const galleryThumbs = computed(() => {
    return [
        ...props.galleries,
        ...props.galleries,
    ]
});

const profile = computed(() => {
    return usePage().props.value.profile;
});

const activeVideo = ref(props.videos[0]);

const aboutImage = ref('url(/storage/home/about.jpg?build=' + props.release.toString() + ')');
const newsroomsImage = ref('url(/storage/home/about-2.jpg?build=' + props.release.toString() + ')');
const evolvingImage = ref('url(/storage/home/evolving.jpg?build=' + props.release.toString() + ')');

const flashMessage = ref(null);
const setFlashMessage = (message) => {
    flashMessage.value = message;
}
const clearFlashMessage = () => {
    flashMessage.value = null;
}
</script>

<template>
    <div>
        <FlashMessage @message="setFlashMessage" @clear="clearFlashMessage" />
        <FeatureWarning />
        <IntroLayout :class="{
            'h-[50rem]': !profile,
            'h-screen': !!profile,
        }" :video="introVideo" :video-poster="introVideoPoster">
            <template v-slot:body>
                <p class="text-sm mb-3 opacity-60 mt-20">Log in using your email address or username, and password</p>
                <div class="my-5">
                    <div v-if="!!flashMessage" class="mb-3 bg-red-500 text-white shadow-lg rounded px-2 py-1">
                        {{ flashMessage }}
                    </div>
                    <LoginForm
                        :errors="errors"
                        :status="status"
                    />
                </div>

                <hr class="my-5" />
                <p class="mt-10">
                    Don't have an account yet? <Link class="text-primary hover:underline" :href="route('register')">Register here for free</Link>.
                </p>
            </template>
        </IntroLayout>
        <template v-if="!profile">

            <section class="bg-tertiary">
                <div class="container px-10 py-20 grid grid-cols-1 lg:grid-cols-3 gap-10 xl:gap-20 text-gray-200 mx-auto">
                    <div class="text-center">
                        <div class="flex justify-center mb-3">
                            <DiscoverIcon class="w-20 ml-4 text-primary" />
                        </div>
                        <h2 class="font-bold text-4xl mb-3">Discover</h2>
                        <p>
                            We hosts well over 400,000 images dating back more than 20 years. Search for and find the
                            exact content you need. Our comprehensive search tools are quick and easy to use.
                        </p>
                    </div>
                    <div class="text-center">
                        <div class="flex justify-center mb-3">
                            <PublishIcon class="w-20 text-primary" />
                        </div>
                        <h2 class="font-bold text-4xl mb-3">Publish</h2>
                        <p>
                            Content publishers can publish to articles and distribute them to carefully crafted and
                            maintained media lists. Get information into the hand of people that matter most to your
                            brand.
                        </p>
                    </div>
                    <div class="text-center">
                        <div class="flex justify-center mb-3">
                            <CommunicateIcon class="w-20 text-primary" />
                        </div>
                        <h2 class="font-bold text-4xl mb-3">Communicate</h2>
                        <p>
                            Engage readers with content that is easy to access and delivered to them at just the right
                            time. Our set of comprehensive tools are designed to help you get right message across.
                        </p>
                    </div>
                </div>
            </section>

            <section
                class="px-10 pt-20 pb-40 bg-cover bg-center relative"
                :style="{
                    backgroundImage: aboutImage,
                }"
            >
                <div class="absolute left-0 right-0 top-0 bottom-0 bg-black md:bg-transparent bg-opacity-50 md:bg-opacity-100 md:bg-gradient-to-r from-transparent to-black" />
                <div class="relative container flex justify-end mx-auto z-10">
                    <div class="md:w-1/3 md:text-right text-gray-200">
                        <h1 class="font-bold text-5xl text-white mb-5">About<br />{{ app.name }}</h1>
                        <div class="prose max-w-none text-white" v-html="content.about"></div>
                    </div>
                </div>
            </section>

            <section
                class="bg-gray-200 px-10 py-20">

                <div class="container mx-auto">
                    <div class="w-1/3 mb-16">
                        <h1 class="font-bold text-5xl mb-5">Latest articles</h1>
                        <div class="prose max-w-none text-black" v-html="content.latest_articles"></div>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-5">
                        <ArticleCard
                            type="div"
                            v-for="article in articles"
                            :article="article"
                            :buttons="false"
                            :rating="false"
                        />
                    </div>
                </div>
            </section>

            <section
                class="px-10 pt-20 pb-40 relative overflow-hidden">
                <div class="absolute -left-20 -right-20 -top-20 -rotate-6 flex flex-wrap items-start gap-0">
                    <img
                        v-for="gallery in galleryThumbs"
                        :src="gallery"
                        class="w-1/6 xl:w-1/12 aspect-square block"
                        alt=""
                    />
                </div>
                <div class="absolute top-0 bottom-0 left-0 right-0 bg-black md:bg-transparent bg-opacity-70 md:bg-opacity-100 md:bg-gradient-to-r from-transparent to-black" />
                <div class="relative z-10 container flex justify-end mx-auto">
                    <div class="md:w-1/2 lg:w-1/3 text-right text-gray-200">
                        <h1 class="font-bold text-5xl mb-5 text-white">
                            Comprehensive<br />media library
                        </h1>
                        <h3 class="font-bold mb-5">
                            Images / Videos / Audio / Copy
                        </h3>
                        <div class="prose max-w-none text-white" v-html="content.media_library"></div>
                    </div>
                </div>
            </section>

            <section class="px-10 py-20" v-if="tv.enabled">
                <div class="container mx-auto md:flex">
                    <div class="md:w-1/3 md:flex flex-col justify-between order-1 md:pl-5">
                        <div>
                            <h1 class="font-bold text-5xl mb-5">MotorPress TV</h1>
                            <div class="prose max-w-none text-black" v-html="content.tv"></div>
                        </div>
                        <div class="grid grid-cols-4 gap-2">
                            <button class="w-full" v-for="video in videos" @click.prevent="activeVideo = video">
                                <img :src="video.thumbnail_url" class="block w-full aspect-square" alt="" />
                            </button>
                        </div>
                    </div>
                    <div class="md:w-2/3 order-0 mt-5 md:mt-0">
                        <video
                            :src="activeVideo?.web_url"
                            class="w-full aspect-video"
                            controls
                        />
                    </div>
                </div>

            </section>

            <section
                class="px-10 pt-48 pb-32 bg-cover bg-center relative"
                :style="{
                    backgroundImage: newsroomsImage,
                }"
            >
                <div class="absolute left-0 right-0 top-0 bottom-0 bg-black md:bg-transparent md:bg-gradient-to-r bg-opacity-60 md:bg-opacity-100 from-black to-transparent" />
                <div class="relative z-10 container mx-auto md:flex text-gray-200">
                    <div class="md:w-1/3">
                        <h1 class="font-bold text-5xl text-white mb-5">
                            Affordable<br />
                            corporate<br />
                            newsrooms
                        </h1>
                        <div class="prose max-w-none text-white" v-html="content.newsrooms"></div>
                    </div>
                </div>
            </section>

            <section
                class="px-10 py-20 bg-white">
                <div class="container mx-auto">
                    <h2 class="font-bold text-4xl mb-5">
                        Trust is earned.
                    </h2>
                    <div class="prose max-w-none text-black" v-html="content.trust"></div>

                    <div class="grid grid-cols-6 lg:grid-cols-10 xl:grid-cols-10 2xl:grid-cols-12 gap-2 mt-10">
                        <Tooltip v-for="organisation in organisations">
                            <template #trigger>
                                <img :src="organisation.thumbnail_url" class="block w-full aspect-square rounded" alt="" />
                            </template>
                            {{ organisation.title }}
                        </Tooltip>
                    </div>
                </div>
            </section>

            <section
                class=" relative px-10 pt-32 pb-20 bg-center bg-cover"
                :style="{
                    backgroundImage: evolvingImage,
                }"
            >
                <div class="absolute left-0 md:left-1/2 right-0 top-0 bottom-0 bg-black md:bg-transparent bg-opacity-50 md:bg-gradient-to-r md:bg-opacity-100 from-transparent to-black" />
                <div class="container mx-auto flex flex-col space-y-20 relative z-10">
                    <div class="flex justify-end">
                        <div class="md:w-1/3 md:text-right text-gray-200">
                            <h1 class="font-bold text-5xl mb-5 text-white">
                                Always<br />
                                evolving
                            </h1>
                            <div class="prose max-w-none text-white" v-html="content.evolving"></div>
                        </div>
                    </div>
                    <div class="md:flex justify-between space-y-20 md:space-y-0">
                        <div class="flex space-x-5 lg:space-x-20 justify-end items-end text-white text-xs lg:text-sm md:order-1 w-2/3">
                            <div class="flex flex-col items-center">
                                <EventPressLogo class="max-h-7 lg:max-h-10 w-full" />
                                <p>
                                    Event & RSVP Management System
                                </p>
                            </div>
                            <div class="flex flex-col items-center">
                                <FleetPressLogo class="max-h-7 lg:max-h-10 w-full" />
                                <p>
                                    Fleet & Vehicle Management System
                                </p>
                            </div>
                        </div>
                        <div class="text-gray-200 md:order-0 w-1/3">
                            <h2 class="font-bold text-white text-3xl xl:text-4xl mb-5">
                                Bring your brand to {{ app.name }}
                            </h2>
                            <p class="mb-5">
                                Ready to join us? We have a space just for you.
                            </p>
                            <FancyButton type="a" href="mailto:info@motorpress.co.za">GET A QUOTE</FancyButton>
                        </div>
                    </div>
                </div>
            </section>
        </template>

        <PageFooter class="-mt-2" />
    </div>
    <span class="text-highlight"></span>
</template>

<style scoped>
</style>
